/**
 * Copyright 2022 Design Barn Inc.
 */

import {
  Provider as ToastProvider,
  Root as ToastRoot,
  Viewport,
  Description as ToastDescription,
  Close,
} from '@radix-ui/react-toast';
import React, { useCallback, useEffect, useState } from 'react';

import type { ToastProps } from './types';

export * from './types';

const Toast: React.FunctionComponent<ToastProps> = ({
  closeText,
  defaultOpen,
  /** default duration from radix-ui/react-toast is 5000 (ms), so we standardize our default with theirs */
  description,
  duration = 5000,
  forceMount,
  onOpenChange,
  open,
}) => {
  const [openToast, setOpenToast] = useState(open);

  useEffect(() => {
    setOpenToast(open);
  }, [open]);

  const handleOnOpenChange = useCallback(
    (isOpen: boolean): void => {
      setOpenToast(isOpen);
      onOpenChange?.(isOpen);
    },
    [setOpenToast, onOpenChange],
  );

  return (
    <ToastProvider duration={duration}>
      <ToastRoot
        className="animate-slide-in flex gap-4 rounded-sm px-4 py-1 text-white bg-green-400"
        open={openToast}
        onOpenChange={handleOnOpenChange}
        defaultOpen={defaultOpen}
        forceMount={forceMount}
        asChild
      >
        <ToastDescription>
          <p className="text-sm">{description}</p>
          {closeText && (
            <Close className="cursor-pointer border-none bg-transparent text-sm font-bold text-white">
              {closeText}
            </Close>
          )}
        </ToastDescription>
      </ToastRoot>
      <Viewport className="fixed z-[100] flex w-[95%] flex-col items-center justify-center gap-4 p-6 outline-none" />
    </ToastProvider>
  );
};

export default Toast;
